/**
 * Phase C — Primavera Sync overview (sidebar page).
 * All projects with their last export/import dates, L3/L4 readiness scores and
 * a sync status, so a PM can see at a glance which schedules are P6-ready.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Network, Loader2, ArrowRight } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { apiGet } from '../../lib/api';
import { formatDate as fmtDate } from '../../lib/format';
const daysSince = (iso) => (iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86400000) : null);

function Pct({ pct }) {
  if (pct == null) return <span className="text-slate-300">—</span>;
  const c = pct >= 90 ? 'text-emerald-600' : pct >= 60 ? 'text-amber-600' : 'text-rose-600';
  return <span className={`font-display font-bold tabular-nums ${c}`}>{pct}%</span>;
}

const STATUS_STYLE = {
  'Never synced': 'bg-slate-100 text-slate-500',
  'Up to date': 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  'Schedule updated since export': 'bg-amber-50 text-amber-700 border border-amber-200',
  'No schedule': 'bg-slate-50 text-slate-400 border border-slate-200',
};

export default function PrimaveraSync() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [projects, logRes] = await Promise.all([
          apiGet('/projects'),
          apiGet('/primavera/sync-log'),
        ]);
        const logs = logRes.items || [];
        const compliance = await Promise.all(
          projects.map((p) => apiGet(`/primavera/compliance-report/${p.id}`).catch(() => null)),
        );
        const cpms = await Promise.all(
          projects.map((p) => apiGet(`/primavera/cpm/${p.id}`)
            .then((r) => ({ ok: true, ...r }))
            .catch((e) => ({ ok: false, status: e?.response?.status, detail: e?.response?.data?.detail }))),
        );
        const built = projects.map((p, i) => {
          const projLogs = logs.filter((l) => l.project_id === p.id);
          const lastExport = projLogs.find((l) => l.direction === 'export');
          const lastImport = projLogs.find((l) => l.direction === 'import');
          const comp = compliance[i];
          const c = cpms[i];
          let cpm = { label: '—', tone: 'text-slate-300' };
          if (c.ok && c.summary?.has_predecessor_links && c.summary.critical_tasks_count > 0) {
            cpm = { label: `✓ ${c.summary.critical_path_duration_days}d critical path`, tone: 'text-emerald-600' };
          } else if (c.ok) {
            cpm = { label: '⚠ No links', tone: 'text-amber-600' };
          } else if (c.status === 400) {
            cpm = { label: '✗ Circular', tone: 'text-rose-600' };
          }
          let status = 'No schedule';
          if (comp) {
            if (!lastExport) status = 'Never synced';
            else {
              const sinceExport = daysSince(lastExport.performed_at);
              status = (comp.last_updated_days != null && comp.last_updated_days < sinceExport)
                ? 'Schedule updated since export' : 'Up to date';
            }
          }
          return {
            id: p.id, name: p.name, code: p.code,
            l3: comp?.l3_ready_pct ?? null, l4: comp?.l4_ready_pct ?? null,
            cpm,
            lastExport: lastExport?.performed_at, lastImport: lastImport?.performed_at, status,
          };
        });
        setRows(built);
      } catch { /* noop */ } finally { setLoading(false); }
    })();
  }, []);

  return (
    <div data-testid="primavera-sync-page">
      <PageHeader overline="Projects" title="Primavera Sync"
        subtitle="P6 export/import status & L3/L4 schedule readiness across all projects" />
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-400"><Loader2 size={20} className="animate-spin mx-auto mb-2" />Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-slate-400"><Network size={24} className="mx-auto mb-2" />No projects yet.</div>
        ) : (
          <table className="w-full text-sm" data-testid="primavera-sync-table">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 font-display font-bold">
              <tr>
                <th className="px-4 py-3 text-left">Project</th>
                <th className="px-4 py-3 text-center">L3</th>
                <th className="px-4 py-3 text-center">L4</th>
                <th className="px-4 py-3 text-left">CPM</th>
                <th className="px-4 py-3 text-left">Last Export</th>
                <th className="px-4 py-3 text-left">Last Import</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`primavera-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-display font-semibold text-slate-900">{r.name}</div>
                    <div className="text-[11px] text-slate-400 font-mono">{r.code}</div>
                  </td>
                  <td className="px-4 py-3 text-center"><Pct pct={r.l3} /></td>
                  <td className="px-4 py-3 text-center"><Pct pct={r.l4} /></td>
                  <td className={`px-4 py-3 text-xs font-display font-semibold ${r.cpm.tone}`} data-testid={`cpm-cell-${r.id}`}>{r.cpm.label}</td>
                  <td className="px-4 py-3 text-slate-600">{fmtDate(r.lastExport)}</td>
                  <td className="px-4 py-3 text-slate-600">{fmtDate(r.lastImport)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[11px] px-2 py-1 rounded-full font-display font-semibold ${STATUS_STYLE[r.status]}`}>{r.status}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/app/projects/${r.id}`} className="inline-flex items-center gap-1 text-xs font-display font-bold text-[#3b4872] hover:underline">
                      Open <ArrowRight size={12} />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
